import type { UiAsset } from '@whiz-cart/node-shared/models/erp/uiAsset';

/**
 * A strand whose newest record is this much older than the controller's newest record is no longer part of the
 * status payload (disabled in the controller configuration, or the asset lists more strands than the box has).
 * The backend only ever adds or overwrites state keys, so such strands keep their last values with a frozen ts.
 */
export const CHARGER_REPORT_STALE_MS = 10 * 60 * 1000;

export type ChargerIssue = 'shortCircuit' | 'notConnected';

export interface ChargerIssues {
    shortCircuit: number[];
    notConnected: number[];
    /** the controller has not reported for CHARGER_REPORT_STALE_MS (or never); the strand lists are then empty */
    noData: boolean;
    lastReport: Date | undefined;
}

/** Newest timestamp over all chargingController_* records, undefined when no record carries one. */
export function getNewestReportTs(asset: UiAsset): Date | undefined {
    let newest: Date | undefined;

    for (const [key, record] of Object.entries(asset.state ?? {})) {
        const ts = key.startsWith('chargingController_') ? record?.ts : undefined;

        if (ts instanceof Date && !Number.isNaN(ts.getTime()) && (!newest || ts > newest)) {
            newest = ts;
        }
    }

    return newest;
}

/** The controllers send the key in lower case: charger_N_shortcircuit. */
export function isChargerShortCircuit(asset: UiAsset, index: number) {
    return !!asset.state?.[`chargingController_charger_${index}_shortcircuit`]?.value;
}

export function isChargerConnected(asset: UiAsset, index: number) {
    return !!asset.state?.[`chargingController_charger_${index}_connected`]?.value;
}

export function isChargerOn(asset: UiAsset, index: number) {
    return !!asset.state?.[`chargingController_charger_${index}`]?.value;
}

/** True while the controller as a whole delivered a status within CHARGER_REPORT_STALE_MS. */
export function isControllerReporting(asset: UiAsset, now = new Date(), reference = getNewestReportTs(asset)) {
    return !!reference && now.getTime() - reference.getTime() <= CHARGER_REPORT_STALE_MS;
}

/** True while the controller still includes the strand in its status payload (see CHARGER_REPORT_STALE_MS). */
export function isChargerReported(asset: UiAsset, index: number, reference = getNewestReportTs(asset)) {
    const state = asset.state ?? {};
    const record = state[`chargingController_charger_${index}`];

    if (!record) {
        return false;
    }

    if (!reference) {
        // no timestamp to compare against, benefit of the doubt
        return true;
    }

    // the strand's auxiliary records freeze together with the base record; a missing record or one without ts passes
    const records = [
        record,
        state[`chargingController_charger_${index}_connected`],
        state[`chargingController_charger_${index}_shortcircuit`],
    ];

    return records.every((r) => !r?.ts || reference.getTime() - r.ts.getTime() <= CHARGER_REPORT_STALE_MS);
}

/**
 * A charger (power supply strand) works when the controller currently reports it as connected and free of short
 * circuits. Defective strands stay enabled in the controller on purpose, a broken power supply must not take the
 * whole charging controller out of service: status colour and the on/off toggle only look at the working strands.
 */
export function isChargerWorking(asset: UiAsset, index: number, now = new Date(), reference = getNewestReportTs(asset)) {
    return (
        isControllerReporting(asset, now, reference) &&
        isChargerReported(asset, index, reference) &&
        isChargerConnected(asset, index) &&
        !isChargerShortCircuit(asset, index)
    );
}

export function getChargerState(asset: UiAsset, index: number, now = new Date()) {
    return isChargerWorking(asset, index, now) ? 'ok' : 'error';
}

/** Strands that have a chargingController_charger_N key at all, fresh or frozen. */
function getStateChargerIndexes(asset: UiAsset) {
    return Object.keys(asset.state ?? {})
        .map((key) => key.match(/^chargingController_charger_(\d+)$/)?.[1])
        .filter((index): index is string => index !== undefined)
        .map(Number)
        .sort((a, b) => a - b);
}

/** Strands the asset is supposed to have: the configured charger count, else whatever the state contains. */
export function getChargerIndexes(asset: UiAsset) {
    const count = asset.properties?.chargingController_chargerCount;

    if (count) {
        return Array.from({ length: count }, (_, i) => i + 1);
    }

    return getStateChargerIndexes(asset);
}

/** Strands the controller currently reports, whatever the configured charger count says. */
export function getReportedChargerIndexes(asset: UiAsset) {
    const reference = getNewestReportTs(asset);
    return getStateChargerIndexes(asset).filter((index) => isChargerReported(asset, index, reference));
}

export function getWorkingChargerIndexes(asset: UiAsset, now = new Date()) {
    const reference = getNewestReportTs(asset);
    return getChargerIndexes(asset).filter((index) => isChargerWorking(asset, index, now, reference));
}

/** Green while at least one power supply works, red only when none does. */
export function getChargingControllerState(asset: UiAsset, now = new Date()): 'ok' | 'error' {
    return getWorkingChargerIndexes(asset, now).length > 0 ? 'ok' : 'error';
}

/** Switch position: charging is on when any working power supply is switched on. */
export function isChargingEnabled(asset: UiAsset, now = new Date()) {
    return getWorkingChargerIndexes(asset, now).some((index) => isChargerOn(asset, index));
}

/**
 * A strand the controller does not report is disabled in its configuration (or does not exist): expected, no issue,
 * and its frozen values mean nothing. Otherwise not connected wins over short circuit. A dead supply floats at a few
 * hundred millivolts and trips the controller's short-circuit check too; the missing voltage is the fact that matters,
 * the short-circuit icon is reserved for strands that do carry voltage.
 */
export function getChargerIssue(asset: UiAsset, index: number, reference = getNewestReportTs(asset)): ChargerIssue | undefined {
    if (!isChargerReported(asset, index, reference)) {
        return undefined;
    }

    if (!isChargerConnected(asset, index)) {
        return 'notConnected';
    }

    if (isChargerShortCircuit(asset, index)) {
        return 'shortCircuit';
    }

    return undefined;
}

/**
 * notConnected covers the strands the asset expects; shortCircuit also covers strands the controller reports beyond
 * the configured count, a short circuit is a short circuit.
 */
export function getChargerIssues(asset: UiAsset, now = new Date()): ChargerIssues {
    const reference = getNewestReportTs(asset);
    const issues: ChargerIssues = { shortCircuit: [], notConnected: [], noData: false, lastReport: reference };

    if (!isControllerReporting(asset, now, reference)) {
        // frozen or missing values say nothing about the strands, the controller itself is the problem
        return { ...issues, noData: true };
    }

    const expected = getChargerIndexes(asset);

    for (const index of expected) {
        const issue = getChargerIssue(asset, index, reference);

        if (issue) {
            issues[issue].push(index);
        }
    }

    for (const index of getReportedChargerIndexes(asset)) {
        if (!expected.includes(index) && isChargerShortCircuit(asset, index)) {
            issues.shortCircuit.push(index);
        }
    }

    issues.shortCircuit.sort((a, b) => a - b);

    return issues;
}

export function hasChargerIssues(issues: ChargerIssues) {
    return issues.noData || issues.shortCircuit.length + issues.notConnected.length > 0;
}
